<template>
    <div class="search">
        <form
            id="search"
            class="search__commands display--flex"
            :class="{ 'disable': !canSearch}"
            @submit.prevent="search"
            novalidate
        >
            <div class="search__bar display--flex position--relative">
                <input
                    type="text"
                    class="search__input"
                    :placeholder="$t('global.whatAreYouLookingFor')"
                    :aria-label="$t('global.whatAreYouLookingFor')"
                    v-model="filter"
                    :disabled="!canSearch"
                >
                <span
                    v-if="showReset"
                    class="search__reset position--absolute capitalize pointer"
                    role="button"
                    @click="resetSearch"
                >
                    {{ $t('global.clear') }}
                </span>
                <base-button
                    form="search"
                    type="submit"
                    class="search__button position--absolute background--transparent"
                    :hasHover="false"
                    :customClass="true"
                >
                    <template #button__icon>
                        <icon-lup />
                    </template>
                </base-button>
            </div>
            <icon-filters
                ref="searchFiltersButton"
                @click.native="showFilters = !showFilters"
                class="search__filters__button pointer"
                :title="$t('global.filters')"
            />
            <div
                v-show="showFilters"
                ref="searchFilters"
                class="search__filters shadow--filters position--absolute"
            >
                <div class="search__filters__content container display--grid">
                    <div class="search__filters__icon">
                        <icon-filters class="search__filters__icon__image display--inline-b"/>
                        <div class="search__filters__icon__text color--blue capitalize display--inline-b">{{ $t('global.filters') }}</div>
                    </div>
                    <!-- eslint-disable max-len -->
                    <base-field
                        style="z-index: 5"
                        class="search__leaving-reason"
                        :settings="{
                            label: { class: 'capitalize', text: $t('global.leavingReason') },
                            input: {
                                component: 'InputAutocomplete',
                                name: 'leavingReason',
                                id: 'leavingReason',
                                type: 'text',
                                value: leavingReason,
                                targetClass: 'search__leaving-reasons__input field__input--default field__input--search typography--default color--default',
                                browserAutocomplete: 'off',
                                placeholder: $t('global.selectAnOption'),
                                dynamicData: {
                                    data: drops.leavingReasons
                                },
                                searchField: true,
                                noListener: true
                            },
                            allowFilling: true
                        }"
                    />
                    <base-field
                        style="z-index: 4"
                        class="search__type"
                        :settings="{
                            label: { class: 'capitalize', text: $t('global.associateType') },
                            input: {
                                component: 'InputAutocomplete',
                                name: 'type',
                                id: 'type',
                                type: 'text',
                                value: type,
                                targetClass: 'search__leaving-reasons__input field__input--default field__input--search typography--default color--default',
                                browserAutocomplete: 'off',
                                placeholder: $t('global.selectAnOption'),
                                dynamicData: {
                                    data: drops.types
                                },
                                searchField: true,
                                noListener: true
                            },
                            allowFilling: true
                        }"
                    />
                    <base-field
                        style="z-index: 3"
                        class="search__state"
                        :settings="{
                            label: { class: 'capitalize', text: $t('global.associateState') },
                            input: {
                                component: 'InputAutocomplete',
                                name: 'state',
                                id: 'state',
                                type: 'text',
                                value: state,
                                targetClass: 'search__leaving-reasons__input field__input--default field__input--search typography--default color--default',
                                browserAutocomplete: 'off',
                                placeholder: $t('global.selectAnOption'),
                                dynamicData: {
                                    data: drops.states
                                },
                                searchField: true,
                                noListener: true
                            },
                            allowFilling: true
                        }"
                    />
                    <base-field
                        style="z-index: 2"
                        class="search__regions"
                        v-model="regions"
                        :settings="{
                            label: { class: 'capitalize', text: $t('global.regions') },
                            input: {
                                component: 'InputMultiOptionsDrop',
                                name: 'regions',
                                id: 'regions',
                                type: 'text',
                                value: regions,
                                targetClass: 'search__regions__input field__input--default field__input--search typography--default color--default',
                                browserAutocomplete: 'off',
                                placeholder: $t('global.regions'),
                                dynamicData: {
                                    data: drops.regions
                                },
                                searchField: true,
                                noListener: true
                            },
                            resetOptions,
                            allowFilling: true
                        }"
                    />
                    <base-field
                        class="search__admission-date"
                        :settings="{
                            label: { class: 'capitalize', text: $t('global.admissionDate') },
                            input: {
                                component: 'InputRangeCalendar',
                                name: 'admissionDate',
                                id: 'admissionDate',
                                targetClass: 'search__calendar__input field__input--default typography--default color--default',
                                value: admissionDate,
                                browserAutocomplete: 'off',
                                dynamicData: {
                                    configs: {
                                        format: 'YYYY-MM-DD',
                                    }
                                },
                                noListener: true,
                                searchField: true
                            },
                            allowFilling: true
                        }"
                    />
                    <base-field
                        class="search__leaving-date"
                        :settings="{
                            label: { class: 'capitalize', text: $t('global.leavingDate') },
                            input: {
                                component: 'InputRangeCalendar',
                                name: 'leavingDate',
                                id: 'leavingDate',
                                targetClass: 'search__calendar__input field__input--default typography--default color--default',
                                value: leavingDate,
                                browserAutocomplete: 'off',
                                dynamicData: {
                                    configs: {
                                        format: 'YYYY-MM-DD',
                                    }
                                },
                                noListener: true,
                                searchField: true
                            },
                            allowFilling: true
                        }"
                    />
                    <base-field
                        style="z-index: 1"
                        class="search__missing-date"
                        :settings="{
                            label: { class: 'capitalize', text: $t('global.missingPayment') },
                            input: {
                                component: 'InputAutocomplete',
                                name: 'missingDate',
                                id: 'missingDate',
                                type: 'text',
                                value: missingDate,
                                targetClass: 'search__missing-date__input field__input--default field__input--search typography--default color--default',
                                browserAutocomplete: 'off',
                                placeholder: $t('global.selectAnOption'),
                                dynamicData: {
                                    data: drops.paymentYears
                                },
                                searchField: true,
                                noListener: true
                            },
                            allowFilling: true
                        }"
                    />
                </div>
            </div>
        </form>
    </div>
</template>

<script>
// packages
import {
    reactive,
    toRefs,
    watch,
    computed,
    onMounted,
    onUnmounted
} from '@vue/composition-api';

// store
import { mapState, useActions } from '@/hooks/use-store';

// consts
import {
    associateTypes,
    associateStates,
    leavingReasons,
    regions
} from '@/configs/const';

// i18n
import i18n from '@/plugins/i18n';

export default {
    props: {
        canSearch: Boolean
    },
    components: {
        BaseButton: () => import( /* webpackChunkName: 'BaseButton' */ '@/components/bundles/buttons/BaseButton' ),
        IconLup: () => import( /* webpackChunkName: 'IconLup' */ '@/components/bundles/layout/images/icons/IconLup' ),
        IconFilters: () => import( /* webpackChunkName: 'IconFilters' */ '@/components/bundles/layout/images/icons/IconFilters' ),
        BaseField: () => import( /* webpackChunkName: 'BaseField' */ '@/components/bundles/fields/BaseField' )
    },
    setup ( _, { root: { $route, $router } }) {
        // Obter ações da store
        const { actions } = useActions();

        const elementRefs = reactive({
            searchFilters: null,
            searchFiltersButton: null
        });

        // configurações de pesquisa.
        const settings = reactive({
            showFilters: false,
            resetOptions: false,
            filter: '',
            regions: [],
            leavingReason: '',
            missingDate: '',
            type: '',
            state: '',
            admissionDate: [],
            leavingDate: []
        });

        const startYear = 1950;
        const currentYear = new Date().getFullYear();
        const years = [ ...Array( currentYear - startYear + 1 ).keys() ].map( x => x + startYear ).reverse();

        const drops = reactive({
            regions: regions.map( key => ({ value: key, label: key }) ),
            leavingReasons: leavingReasons.map( key => ({ value: key, label: key }) ),
            types: Object.values( associateTypes ).map( key => ({ value: key, label: i18n.t( `associateTypes.${key}` ) }) ),
            states: Object.values( associateStates ).map( key => ({ value: key, label: i18n.t( `associateStates.${key}` ) }) ),
            paymentYears: years.map( year => ({ value: `${year}`, label: `${year}` }) )
        });

        const searchData = mapState({ module: 'storePayload', key: 'searchData' });

        const showReset = computed( () => settings.filter.length > 0 );

        /**
         * Pesquisa
         * Adiciona ou altera a query da rota em
         * que o utilizador está, com o termo inserido na input
         * ou as opções selecionadas nas drops.
         *
        */
        const search = () => {
            const newRoute = {
                query: {
                    ...( settings.filter.length > 0 && { search: settings.filter }),
                    ...searchData.value
                }
            };

            $router.push({ ...newRoute });
        };

        /**
         * Reset pesquisa de texto
         * Limpa o valor da input de texto e a
         * query da route.
         *
        */
        const resetSearch = () => {
            settings.filter = '';
            // eslint-disable-next-line
            const { search, ...queryWithoutSearch } = $route.query;
            $router.push({ query: queryWithoutSearch });
        };

        /**
         * Reset de toda a pesquisa.
         *
        */
        const resetAllSearch = () => {
            settings.filter = '';
            settings.regions = [];
            settings.leavingReason = '';
            settings.missingDate = '';
            settings.type = '';
            settings.state = '';
            settings.admissionDate = [];
            settings.leavingDate = [];

            // reset da pesquisa na store.
            actions( 'resetSearchData' );

            // Resetar drops ao mudar de página.
            settings.resetOptions = true;
            setTimeout( () => {
                settings.resetOptions = false;
            }, 300 );

            $router.push({ query: {} });
        };

        // Sempre que o utilizador altere a rota
        // irá validar se já existe alguma query
        watch( () => $route,
            ({ query }) => {
                /* eslint-disable */
                const {
                    search,
                    leavingReason,
                    missingDate,
                    type,
                    state,
                    sort,
                    direction,
                    ...arrayParams
                } = query;
                /* eslint-enable */

                const filtersNotArray = {
                    search,
                    leavingReason,
                    missingDate,
                    type,
                    state,
                    sort,
                    direction
                };

                Object.keys( filtersNotArray ).forEach( key => {
                    actions( 'saveSearchData', { [key]: filtersNotArray[key] });
                    settings[key] = filtersNotArray[key] || '';
                });

                Object.keys( arrayParams ).forEach( key => {
                    const varIsArray = Array.isArray( arrayParams[key] );

                    actions( 'saveSearchData', { [key]: ( varIsArray ) ? arrayParams[key] : [ arrayParams[key] ] });
                    settings[key] = ( varIsArray ) ? arrayParams[key] : [ arrayParams[key] ];
                });
            }, {
                immediate: true
            });

        // Sempre que o utilizador altere a página
        // irá resetar toda a pesquisa
        watch( () => $route.name,
            newRouteName => {
                if ( newRouteName )
                    resetAllSearch();
            });

        watch( () => searchData.value,
            ( newVal, oldVal ) => {
                if ( JSON.stringify( newVal ) !== JSON.stringify( oldVal ) )
                    search();
            }, {
                deep: true
            });

        // handler on click outside filters
        const handleClickOutside = event => {
            if ( !elementRefs.searchFiltersButton.$el.contains( event.target )
                && !elementRefs.searchFilters.contains( event.target ) && settings.showFilters )
                settings.showFilters = false;
        };

        onMounted( () => {
            document.addEventListener( 'click', handleClickOutside );
        });

        onUnmounted( () => {
            document.removeEventListener( 'click', handleClickOutside );
        });

        return {
            ...toRefs( settings ),
            ...toRefs( elementRefs ),
            showReset,
            search,
            resetSearch,
            drops
        };
    }
};
</script>

<style lang="scss">
    // =============================================================================
    // Style
    // =============================================================================
    .search {
        &__bar {
            width: 100%;
        }
        &__commands {
            gap: 12px;
        }
        &__input {
            @include dimensions( 100%, 45px );
            @include elements-colors ( $color-grey-light, $color-black );
            caret-color: $color-blue;
            @include rounded-corner( 5px );
            padding: 0 75px 0 48px;
            @include placeholder {
                color: $color-grey;
            }
        }
        &__reset {
            @include transform-position( 50%, 15px, false, false, translateY( -50% ) );
        }
        &__button {
            height: 20px;
            @include transform-position( 50%, false, false, 20px, translateY( -50% ) );
            line-height: unset;
        }
        &__projects {
            min-width: 215px;
        }
        &__filters {
            @include dimensions( 100%, auto );
            @include elements-colors( $color-white, $color-grey );
            @include transform-position( 100px, 0, false, 0 );
            padding: 50px 0 68px;
            &__content {
                grid-template: auto / repeat( 5, 1fr );
                padding: 0 42px;
                max-width: 1350px;
                margin: 0 auto;
            }
            &__icon {
                grid-row: 1 / 3;
                &__text {
                    @include typography-specs( 20px, 26px, 500 );
                    margin-left: 10px;
                }
            }
            &__button {
                margin: auto;
            }
        }
    }
</style>